/**
 * Drop-in replacement for firebase/functions' `httpsCallable` that reports
 * failed calls to Discord (see errorReporting.ts) before rethrowing the
 * original error unchanged, so callers' error handling is unaffected.
 *
 * Import `httpsCallable` from here, not from 'firebase/functions'. The call
 * site is the only place that knows which function failed and with what
 * payload, and pages usually catch the error and show a toast, so it would
 * never reach a global handler.
 *
 * Reporting from the browser as well as the server is deliberate. Some
 * failures never reach the function: a missing IAM invoker binding shows up
 * as a CORS error (see "Known deploy flakiness" in CLAUDE.md), and networks
 * fail. For failures the server did log, the client report adds who hit it,
 * on which page, and with what request.
 */

import {
  httpsCallable as firebaseHttpsCallable,
  type Functions,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions'
import { reportError } from './errorReporting'

/**
 * Codes the backend raises for ordinary user-facing outcomes (an expired
 * reset link is `not-found`, a record at its label cap is
 * `resource-exhausted`). Everything else is reported, including
 * `invalid-argument` and `permission-denied`: from this app's own UI those
 * mean a frontend bug, or someone probing.
 */
export const UNREPORTED_CALLABLE_CODES = new Set([
  'functions/cancelled',
  'functions/not-found',
  'functions/already-exists',
  'functions/resource-exhausted',
  'functions/aborted',
  'functions/out-of-range',
])

/**
 * Latched once the page starts going away. A callable in flight when the user
 * reloads or navigates has its fetch aborted by the browser, and that arrives
 * here as the SDK shape below. `pagehide` rather than `unload` because it also
 * covers the back/forward-cache path.
 */
let pageIsUnloading = false
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageIsUnloading = true
  })
  window.addEventListener('beforeunload', () => {
    pageIsUnloading = true
  })
}

/**
 * The Firebase SDK's own fallback when `fetch` itself fails, rather than the
 * function returning an error: code `functions/internal` with the message
 * literally `internal` (lower case). A real server-side failure doesn't look
 * like this - an unhandled exception in the function surfaces as `INTERNAL`
 * (upper case, generated by firebase-functions) and an explicit
 * `HttpsError('internal', ...)` carries its own message.
 */
export function isSdkFetchFailure(error: unknown): boolean {
  const e = (error ?? {}) as { code?: unknown; message?: unknown }
  return e.code === 'functions/internal' && e.message === 'internal'
}

export function shouldReportCallableError(
  error: unknown,
  pageUnloading: boolean = pageIsUnloading
): boolean {
  const code = (error as { code?: unknown } | null)?.code
  if (typeof code === 'string' && UNREPORTED_CALLABLE_CODES.has(code)) return false

  // A request the browser aborted because the page is going away (GitHub #82).
  // Nothing failed that anyone can act on, and the 20s waits in #81 make it
  // easy to provoke - one impatient reload produced three alerts for three
  // requests that all went on to return 200.
  //
  // Deliberately requires BOTH conditions. A fetch failure on a page that is
  // staying put is exactly how a missing IAM invoker binding presents (the
  // CORS case in the file comment above) and how a real network outage
  // presents, and those are worth knowing about - so the SDK shape alone is
  // never enough to stay silent.
  if (pageUnloading && isSdkFetchFailure(error)) return false

  return true
}

export function httpsCallable<RequestData = unknown, ResponseData = unknown, StreamData = unknown>(
  functionsInstance: Functions,
  name: string,
  options?: HttpsCallableOptions
): HttpsCallable<RequestData, ResponseData, StreamData> {
  const callable =
    options === undefined
      ? firebaseHttpsCallable<RequestData, ResponseData, StreamData>(functionsInstance, name)
      : firebaseHttpsCallable<RequestData, ResponseData, StreamData>(functionsInstance, name, options)

  const wrapped = (async (data?: RequestData | null) => {
    try {
      return await callable(data)
    } catch (error) {
      if (shouldReportCallableError(error)) {
        const { code, details } = (error ?? {}) as { code?: string; details?: unknown }
        reportError('api', error, { callable: { name, code, details }, request: data })
      }
      throw error
    }
  }) as HttpsCallable<RequestData, ResponseData, StreamData>
  wrapped.stream = callable?.stream

  return wrapped
}
